import React from 'react';

/* ============================================================================
   ErrorBoundary — the difference between "one card is broken" and "the site is
   gone".

   WHY THIS EXISTS. On 2026-08-25 the model wrote `recommendations` as a JSON
   string instead of an array. `ZanaSummaryCard` called `.find()` on it, threw,
   and because React 18 unmounts the ENTIRE tree on an uncaught render error and
   this app had no boundary anywhere, trasseapp.com/fleet rendered pure white.
   No message, no header, no way back — and no way for the owner to tell anyone
   what had happened. A bad field in one dashboard card took down fleet
   tracking, trips, the live map and everything else.

   A boundary makes that impossible: the failing subtree is replaced by a small
   panel that NAMES the error, and the rest of the page keeps working.

   Showing the real message is deliberate. This is an internal fleet-operations
   dashboard whose users report problems to us; "Something went wrong" would
   have cost the same round-trip that this incident cost.
   ========================================================================== */

interface Props {
  children: React.ReactNode;
  /** What broke, in the owner's words — "Zana's summary", "the fleet page". */
  label?: string;
  /** Rendered instead of the default panel, when a caller wants it silent. */
  fallback?: React.ReactNode;
}

interface State { error: Error | null }

export default class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Keep the stack in the console for whoever is looking at DevTools.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary] ${this.props.label || 'component'} failed:`, error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const what = this.props.label || 'This section';
    return (
      <div
        role="alert"
        style={{
          border: '1px solid #f0c9c9',
          background: '#fdf4f4',
          borderRadius: 12,
          padding: '14px 16px',
          margin: '8px 0',
          fontFamily: "'Nunito', sans-serif",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 14, color: '#a33a30' }}>
          {what} could not be displayed
        </div>
        <div style={{ fontSize: 13, color: '#6b5f5f', marginTop: 4, lineHeight: 1.5 }}>
          The rest of the page is unaffected. If this keeps happening, send this line to support:
        </div>
        <code
          style={{
            display: 'block',
            marginTop: 8,
            fontSize: 12,
            color: '#7a3b34',
            background: '#fbeaea',
            padding: '8px 10px',
            borderRadius: 8,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {String(error.message || error)}
        </code>
        <button
          type="button"
          onClick={() => this.setState({ error: null })}
          style={{
            marginTop: 10, padding: '7px 14px', borderRadius: 9, border: '1px solid #e3c4c4',
            background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#a33a30',
          }}
        >
          Try again
        </button>
      </div>
    );
  }
}
